import { ChevronRight, Mail } from 'react-feather';
import { useHistory } from 'react-router-dom';
import {
  daysAgo,
  getDayDateTimeFromEntryDateTitle,
  getDayStringFromCreationDate,
  getNumberOfDaysSinceCreationDateString,
  sameDay
} from '../../util/dateTime';

export type RecentEntry = {
  name: string;
  imported: boolean;
  tags: string[];
  /** First words of the entry, already stripped of markup. Empty if blank. */
  text: string;
};

/*
 * The grid tells you *that* you wrote; it never tells you what about. A list of
 * the last few entries is the cheapest way to make the page feel like your
 * journal rather than a dashboard, and it is the natural home for the
 * write-on-paper nudge, which was previously only discoverable in Settings.
 */

const relativeLabel = (entryName: string) => {
  if (sameDay(new Date(), entryName)) return 'Today';
  if (sameDay(daysAgo(1), entryName)) return 'Yesterday';
  if (getNumberOfDaysSinceCreationDateString(entryName) <= 7) return getDayStringFromCreationDate(entryName);
  return '';
};

const EntryRow = (props: { entry: RecentEntry; onOpen: (entryName: string) => void }) => {
  const { entry, onOpen } = props;
  const { date } = getDayDateTimeFromEntryDateTitle(entry.name);
  const relative = relativeLabel(entry.name);

  return (
    <button
      onClick={() => onOpen(entry.name)}
      className="tw-flex tw-w-full tw-items-start tw-gap-4 tw-rounded-2xl tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-bg-transparent tw-px-4 tw-py-4 tw-text-left tw-cursor-pointer hover:tw-bg-neutral-50 last:tw-border-b-0"
    >
      <div className="tw-hidden md:tw-block tw-w-28 tw-shrink-0">
        <div className="tw-text-sm tw-font-semibold tw-text-neutral-800">{date}</div>
        <div className="tw-text-xs tw-text-neutral-500">{relative}</div>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2 tw-min-w-0 tw-flex-1">
        <div className="md:tw-hidden tw-text-sm tw-font-semibold tw-text-neutral-800">
          {date} <span className="tw-font-normal tw-text-neutral-500">{relative}</span>
        </div>
        <div className="tw-text-base tw-text-neutral-700 tw-truncate">
          {entry.text || <span className="tw-text-neutral-400">Empty entry</span>}
        </div>
        {(entry.tags.length > 0 || entry.imported) && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            {entry.tags.filter(Boolean).map((tag) => (
              <span
                key={tag}
                className="tw-rounded tw-bg-primary-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-primary-700"
              >
                {tag}
              </span>
            ))}
            {entry.imported && (
              <span className="tw-flex tw-items-center tw-gap-1 tw-rounded tw-bg-secondary-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-secondary-600">
                <Mail className="tw-w-3 tw-h-3" /> from a photo
              </span>
            )}
          </div>
        )}
      </div>
      <ChevronRight className="tw-w-4 tw-h-4 tw-shrink-0 tw-mt-1 tw-text-neutral-300" />
    </button>
  );
};

const ImportByEmailNudge = () => {
  const history = useHistory();
  return (
    <button
      onClick={() => history.push('/settings')}
      className="tw-flex tw-w-full tw-flex-col tw-gap-3 tw-rounded-2xl tw-border-none tw-bg-neutral-50 tw-p-4 tw-text-left tw-cursor-pointer md:tw-flex-row md:tw-items-center md:tw-justify-between"
    >
      <div className="tw-flex tw-items-start tw-gap-3 tw-min-w-0">
        <Mail className="tw-w-5 tw-h-5 tw-shrink-0 tw-mt-0.5 tw-text-neutral-500" />
        <div className="tw-text-sm tw-text-neutral-600">
          Still write on paper? Email a photo to your personal Jumble address and it gets transcribed and filed by date.
        </div>
      </div>
      <div className="tw-shrink-0 tw-text-sm tw-font-semibold tw-text-primary-600">Set it up →</div>
    </button>
  );
};

export const RecentEntries = (props: {
  recent: RecentEntry[];
  isLoading: boolean;
  showImportNudge: boolean;
  onOpen: (entryName: string) => void;
}) => {
  const { recent, isLoading, showImportNudge, onOpen } = props;

  return (
    <div className="tw-rounded-3xl tw-border-2 tw-border-solid tw-border-neutral-100 tw-bg-white tw-p-1">
      {isLoading ? (
        [0, 1, 2].map((i) => (
          <div key={i} className="tw-flex tw-flex-col tw-gap-2 tw-px-4 tw-py-4">
            <div className="tw-h-4 tw-w-24 tw-rounded tw-bg-neutral-100 tw-animate-pulse"></div>
            <div className="tw-h-4 tw-w-3/4 tw-rounded tw-bg-neutral-100 tw-animate-pulse"></div>
          </div>
        ))
      ) : recent.length === 0 ? (
        <div className="tw-px-4 tw-py-6 tw-text-base tw-text-neutral-500">
          Nothing here yet. Your entries will appear as you write them.
        </div>
      ) : (
        recent.map((entry) => <EntryRow key={entry.name} entry={entry} onOpen={onOpen} />)
      )}
      {showImportNudge && <div className="tw-p-3 tw-pt-1">{<ImportByEmailNudge />}</div>}
    </div>
  );
};
